<template>
	<div v-if="loaded" class="container organisation-registration-form">
		<h1>{{ formConfig.title }}</h1>

		<div class="mb-2 mt-2" v-html="formConfig.description" />

		<b-alert :show="!!errorMsg" variant="danger">{{ errorMsg }}</b-alert>

		<b-alert v-if="showSuccess" :show="true" variant="success">
			<div
				v-if="formConfig.thankYouMessage"
				v-html="formConfig.thankYouMessage"
			/>
			<div v-else>{{ $t("success") }}</div>
		</b-alert>

		<b-overlay v-else v-show="!showPaymentStep" :show="saving">
			<validation-observer v-slot="{ handleSubmit }">
				<b-form class="d-flex flex-column" @submit.prevent="handleSubmit(submit)">
					<b-row
						v-for="formModule in formConfig.fieldsConfiguration"
						:key="formModule.name"
						:class="'input-' + formModule.name + '-container'"
						class="mt-2"
					>
						<b-col>
							<div v-html="formModule.description" />

							<financial-details-module
								v-if="formModule.name === 'FinancialDetails'"
								:input="input"
								:form-config="formConfig"
								:form-module="formModule"
							/>

							<organisation-plans-module
								v-else-if="
									formModule.name === 'OrganisationPlanAssignment' &&
										formConfig.allowMultiplePlans
								"
								:input="input"
								:form-config="formConfig"
								:form-module="formModule"
							/>

							<generic-fields-module
								v-else-if="!formModule.isCustom"
								:input="input"
								:form-config="formConfig"
								:form-module="formModule"
							/>

							<files-module
								v-else-if="formModule.isCustom && formModule.name === 'Files'"
								v-model="input.Files"
							/>

							<b-row
								v-if="
									formModule.name === 'OrganisationPlanAssignment' &&
										!formConfig.allowMultiplePlans &&
										showSinglePlanAmount
								"
								class="organisation-plan-amount"
							>
								<b-col cols="12" md="6">
									<b-list-group>
										<b-list-group-item
											v-for="plan in selectedPlans"
											:key="plan.id"
											class="d-flex justify-content-between"
										>
											<span>{{ plan.name }}</span>
											<span class="text-nowrap ml-2">
												{{ formatPlanPrice(getOrganisationPlanPrice(plan, formConfig)) }}
											</span>
										</b-list-group-item>
										<b-list-group-item
											class="d-flex justify-content-between font-weight-bold"
										>
											<span>{{ $t("total") }}</span>
											<span class="text-nowrap ml-2">
												{{ formatPlanPrice(selectedPlansTotal) }}
											</span>
										</b-list-group-item>
									</b-list-group>
								</b-col>
							</b-row>
						</b-col>
					</b-row>

					<b-row v-if="isAgreementEnabled" class="mt-2">
						<b-col>
							<p>
								<validation-provider
									v-slot="{ errors }"
									:rules="{ required: true }"
									:name="$t('conditions_checkbox')"
								>
									<b-form-checkbox v-model="agreement" validatable>
										{{ $t("agree_with_the") }}
										<a
											v-if="formConfig.termsAndConditionsUrl"
											target="_blank"
											:href="formConfig.termsAndConditionsUrl"
											>{{ $t("terms_and_conditions") }}</a
										>
										<template v-else-if="formConfig.termsAndConditionsText">
											{{ $t("terms_and_conditions") }}
										</template>
										<template
											v-if="
												(formConfig.termsAndConditionsUrl ||
													formConfig.termsAndConditionsText) &&
													formConfig.privacyPolicyUrl
											"
										>
											{{ $t("agree_and") }}
										</template>
										<a
											v-if="formConfig.privacyPolicyUrl"
											target="_blank"
											:href="formConfig.privacyPolicyUrl"
										>
											{{ $t("privacy_policy") }}
										</a>
									</b-form-checkbox>
									<p v-for="error in errors" :key="error" class="text-danger mb-0">
										{{ error }}
									</p>
								</validation-provider>
							</p>

							<p
								v-if="formConfig.termsAndConditionsText"
								v-html="formConfig.termsAndConditionsText"
							/>
						</b-col>
					</b-row>

					<b-row v-if="isRecaptchaOnForm" class="mt-2">
						<b-col class="d-flex justify-content-center">
							<base-recaptcha
								:site-key="formConfig.recaptchaSiteKey"
								@verify="onRecaptchaVerify"
							/>
						</b-col>
					</b-row>

					<b-row>
						<b-col class="text-center mb-5">
							<hr />
							<b-btn
								type="submit"
								variant="primary"
								class="continue-button"
								:disabled="saving || (isRecaptchaOnForm && !recaptchaToken)"
							>
								{{ isPaymentModeActive ? $t("continue") : $t("confirm") }}
							</b-btn>
						</b-col>
					</b-row>
				</b-form>
			</validation-observer>
		</b-overlay>

		<organisation-payment-step
			v-if="!showSuccess && showPaymentStep"
			:form-config="formConfig"
			:amount-state="amount"
			:has-selected-plan="hasSelectedPlan"
			:selected-plans="selectedPlans"
			:saving="saving"
			:is-recaptcha-enabled="isRecaptchaEnabled"
			:recaptcha-token="recaptchaToken"
			@retry="loadAmount"
			@recaptcha-verify="onRecaptchaVerify"
			@back="closePaymentStep"
			@confirm="submitRegistration"
		/>

		<b-modal
			id="organisationExistsWarningModal"
			static
			hide-header
			no-close-on-esc
			no-close-on-backdrop
		>
			<div v-html="formConfig.duplicateExistsMessage || $t('organisation_exists')" />
			<template #modal-footer="{ cancel }">
				<b-button variant="secondary" @click="cancel">
					{{ $t("ok") }}
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import { localize } from "vee-validate";
import {
	getOrganisationRegistrationForm,
	submitOrganisationRegistration,
	calculateOrganisationAmount,
	hasSelectedOrganisationPlan,
	isDuplicateOrganisationError,
	getErrorDetail,
	isOrganisationPaymentMode,
} from "@/services/organisationRegistrationFormApi";
import { planMixin, normalizeRedirectUrl } from "@/shared";

export default {
	name: "OrganisationRegistrationForm",
	mixins: [planMixin],
	data() {
		return {
			formConfig: {},
			input: {
				validationToken: null,
			},
			loaded: false,
			saving: false,
			showSuccess: false,
			errorMsg: null,
			agreement: false,
			recaptchaToken: null,
			// FOYS-27334: payment-mode forms only.
			showPaymentStep: false,
			amount: {
				loading: false,
				error: false,
				result: null,
			},
			amountRequestId: 0,
			// amountRequestKey of the last successfully loaded amount.
			loadedAmountRequestKey: null,
		};
	},
	computed: {
		isPaymentModeActive() {
			return isOrganisationPaymentMode(this.formConfig);
		},
		hasSelectedPlan() {
			return hasSelectedOrganisationPlan(this.input.PlanAssignment);
		},
		// The selected plan options ({ id, name, price, totalPrice }), in
		// selection order.
		selectedPlans() {
			if (!this.hasSelectedPlan) {
				return [];
			}

			const planField = (this.formConfig.fieldsConfiguration || [])
				.map((formModule) => formModule.fields || [])
				.flat()
				.find((field) => field.key === "PlanAssignment.organisationPlanId");
			const options = (planField && planField.options) || [];
			const value = this.input.PlanAssignment.organisationPlanId;
			const ids = Array.isArray(value) ? value : [value];

			return ids.map((id) => {
				const option = options.find((x) => String(x.id) === String(id));
				return option || { id, name: String(id) };
			});
		},
		// Payment-mode forms show the selected plan's price (totalPrice) and the
		// total under the single-plan dropdown; no amount call on this step.
		showSinglePlanAmount() {
			return (
				this.isPaymentModeActive && this.hasSelectedPlan
			);
		},
		selectedPlansTotal() {
			return this.selectedPlans.reduce(
				(sum, plan) => sum + (this.getOrganisationPlanPrice(plan, this.formConfig) || 0),
				0
			);
		},
		// Changes to these inputs change the amount; other fields don't.
		amountRequestKey() {
			if (!this.isPaymentModeActive) {
				return null;
			}

			const planAssignment = this.input.PlanAssignment || {};
			const billingInfo = this.input.BillingInfoDto || {};

			return JSON.stringify({
				plans: planAssignment.organisationPlanId ?? null,
				debtCollection: billingInfo.debtCollection ?? null,
				// The IBAN only matters with debt collection on.
				iban: billingInfo.debtCollection ? billingInfo.iban ?? null : null,
				dontInvoiceMemberships: billingInfo.dontInvoiceMemberships ?? null,
			});
		},
		// For payment-mode forms the recaptcha moves to the payment step, where
		// the registration is actually posted.
		isRecaptchaOnForm() {
			return this.isRecaptchaEnabled && !this.isPaymentModeActive;
		},
		isAgreementEnabled() {
			return !!(
				this.formConfig.termsAndConditionsText ||
				this.formConfig.termsAndConditionsUrl ||
				this.formConfig.privacyPolicyUrl
			);
		},
		isRecaptchaEnabled() {
			return !!(
				this.formConfig.recaptchaSiteKey &&
				this.formConfig.recaptchaSiteKey.length > 0
			);
		},
	},
	async created() {
		var dateTimeNow = new Date().toISOString();
		this.input.validationToken = Buffer.from(dateTimeNow).toString("base64");

		await this.loadFormConfig();

		if (this.errorMsg) {
			return;
		}
	},
	methods: {
		getConfigId() {
			let registrationFormEntry = document.querySelectorAll(
				"registration-form-entry"
			)[0];
			if (typeof registrationFormEntry === "undefined") {
				throw new Error('"registration-form-entry" html element is not found');
			}
			let configId = registrationFormEntry.getAttribute("configuration");
			if (!configId) {
				throw new Error(
					'"registration-form-entry" configuration property is not set'
				);
			}

			return configId;
		},
		async loadFormConfig() {
			try {
				this.formConfig = await getOrganisationRegistrationForm(this.getConfigId());
				this.$i18n.locale = this.formConfig.locale;
				localize(this.formConfig.locale.substring(0, 2));
				this.loaded = true;
			} catch (error) {
				// 404 = form id not found / not an Organisation-typed form.
				this.errorMsg = getErrorDetail(error) || this.$t("something_went_wrong");
				console.log(error);
			}
		},
		onRecaptchaVerify(token) {
			this.recaptchaToken = token;
		},
		formatPlanPrice(amount) {
			return this.formatOrganisationPlanPrice(amount || 0, this.formConfig.currencyCode);
		},
		resetAmount() {
			this.amount.loading = false;
			this.amount.error = false;
			this.amount.result = null;
			this.loadedAmountRequestKey = null;
		},
		// FOYS-27334: the amount is only calculated for the payment step: when it
		// opens (skipped when plans and direct-debit inputs are unchanged since
		// the last successful load) and on retry.
		async loadAmount() {
			// Latest request wins: responses of older requests are ignored.
			const requestId = ++this.amountRequestId;

			if (!this.hasSelectedPlan) {
				this.resetAmount();
				return;
			}

			const requestKey = this.amountRequestKey;

			// Plans or direct-debit inputs changed: drop the old lines so only the
			// loader shows, not the previous selection's amounts.
			if (requestKey !== this.loadedAmountRequestKey) {
				this.amount.result = null;
				this.loadedAmountRequestKey = null;
			}

			this.amount.loading = true;
			this.amount.error = false;

			try {
				const result = await calculateOrganisationAmount(
					this.formConfig.id,
					this.input
				);

				if (requestId === this.amountRequestId) {
					this.amount.result = result;
					this.loadedAmountRequestKey = requestKey;
				}
			} catch (error) {
				// Any error = the amount could not be calculated. Show a retry; the
				// registration can still be submitted.
				if (requestId === this.amountRequestId) {
					this.amount.result = null;
					this.amount.error = true;
					this.loadedAmountRequestKey = null;
				}
				console.log(error);
			} finally {
				if (requestId === this.amountRequestId) {
					this.amount.loading = false;
				}
			}
		},
		submit() {
			if (!this.isPaymentModeActive) {
				return this.submitRegistration();
			}

			this.errorMsg = null;
			this.recaptchaToken = null;
			this.showPaymentStep = true;

			if (
				!this.amount.result ||
				this.loadedAmountRequestKey !== this.amountRequestKey
			) {
				this.loadAmount();
			}
		},
		closePaymentStep() {
			this.errorMsg = null;
			this.recaptchaToken = null;
			this.showPaymentStep = false;
		},
		async submitRegistration(paymentChoice = null) {
			this.errorMsg = null;
			this.saving = true;

			try {
				const response = await submitOrganisationRegistration(this.formConfig.id, {
					...this.input,
					recaptchaToken: this.recaptchaToken,
					paymentChoice: paymentChoice || undefined,
				});

				const successRedirectUrl = normalizeRedirectUrl(this.formConfig.successRedirectUrl);

				if (response.paymentLink) {
					window.location.href = response.paymentLink;
				} else if (successRedirectUrl) {
					window.location.href = successRedirectUrl;
				} else {
					this.showSuccess = true;
					this.showPaymentStep = false;
				}

				return response;
			} catch (error) {
				if (isDuplicateOrganisationError(error)) {
					this.$bvModal.show("organisationExistsWarningModal");
				} else {
					this.errorMsg = getErrorDetail(error) || this.$t("something_went_wrong");
				}
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>
